import { type RefObject, useEffect, useState } from 'react';

export interface ElementDimensions {
  width: number;
  height: number;
}

/**
 * Hook to measure an element's dimensions and track resize changes
 * @param ref - React ref to the element to measure
 * @returns The current dimensions of the element
 */
export function useElementDimensions(ref: RefObject<HTMLElement | null>): ElementDimensions {
  const [dimensions, setDimensions] = useState<ElementDimensions>({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateDimensions = () => {
      setDimensions({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [ref]);

  return dimensions;
}
